import type { ReactElement, ReactNode, RefObject } from 'react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { kebabCase } from 'lodash-es'
import cc from 'classcat'

import { DraggableSettingsLayerWrapper } from './DraggableSettingsLayerWrapper'
import { Popover } from '../../Popover'
import type { PopoverAlign, PopoverSide } from '../../Popover/types'
import { Tooltip } from '../../Tooltip'
import preventFileDrop from '../../../utils/preventFileDrop'

interface Tab {
  name: string
  title: string
  active: boolean
  onClick: (name: string) => void
}

interface SettingsLayerLayoutProps {
  readonly forwardedRef?: React.ForwardedRef<HTMLDivElement>
  readonly title?: string
  readonly explanation?: string
  readonly tabs?: Tab[]
  readonly body?: ReactNode
  readonly footer?: ReactElement
}

function SettingsLayerLayoutRaw(props: SettingsLayerLayoutProps) {
  return (
    <>
      <div className="dali-settingslayer-title" ref={props.forwardedRef} onDragOverCapture={preventFileDrop}>
        <span>
          {props.title}
          {props.explanation ? <Tooltip content={props.explanation} /> : null}
        </span>
      </div>
      <div className="dali-settingslayer-main" onDragOverCapture={preventFileDrop}>
        {props.tabs && (
          <div className="dali-settingslayer-tabs">
            {props.tabs.map((tab) => (
              <button
                key={tab.name}
                type="button"
                className={cc([`dali-settingslayer-tabs-${kebabCase(tab.name)}`, { active: tab.active }])}
                data-testid={`settings-layer-tab-${kebabCase(tab.name)}`}
                title={tab.title}
                onClick={() => tab.onClick(tab.name)}
              />
            ))}
          </div>
        )}
        {props.tabs ? (
          <scroll-shadow>
            <div className="dali-settingslayer-main-content">{props.body}</div>
          </scroll-shadow>
        ) : (
          props.body
        )}
      </div>
      {props.footer && (
        <div className="dali-settingslayer-footer" onDragOverCapture={preventFileDrop}>
          {props.footer}
        </div>
      )}
    </>
  )
}

export const SettingsLayerLayout = forwardRef<HTMLDivElement, SettingsLayerLayoutProps>((props, ref) => (
  <SettingsLayerLayoutRaw {...props} forwardedRef={ref} />
))

SettingsLayerLayout.displayName = 'SettingsLayerLayout'

interface SettingsLayerProps {
  readonly children: ReactNode | (({ renderLayout }: any) => ReactNode)
  readonly className?: string
  readonly onEscapeKeyDown?: (event: KeyboardEvent) => void
  readonly virtualRef?: RefObject<HTMLElement | null>
  readonly open?: boolean
  readonly side?: PopoverSide
  readonly align?: PopoverAlign
  readonly hideArrow?: boolean
  readonly draggable?: boolean
  readonly sideOffset?: number
  readonly onActiveStateChange?: (isActive: boolean) => void
}

export default function SettingsLayer({
  onActiveStateChange,
  children,
  className,
  onEscapeKeyDown,
  hideArrow = true,
  virtualRef,
  open,
  side,
  align = 'start',
  draggable = true,
  sideOffset = 40,
}: SettingsLayerProps) {
  const dragAnchorRef = useRef<HTMLDivElement>(null)
  const [isActive, setIsActive] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const handleMouseDown = () => {
    setIsActive(true)
    onActiveStateChange?.(true)
  }

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current) {
        const isOutsideClick = !ref.current.contains(e.target as Node)
        if (isOutsideClick) {
          setIsActive(false)
          onActiveStateChange?.(false)
        }
      }
    }

    setIsActive(!!open)
    onActiveStateChange?.(!!open)

    window.addEventListener('mousedown', handleClick)
    return () => window.removeEventListener('mousedown', handleClick)
  }, [open, onActiveStateChange])

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    onEscapeKeyDown?.(event)
  }

  const renderLayout = ({ title, explanation, tabs, body, footer }: Partial<SettingsLayerLayoutProps> = {}) => (
    <SettingsLayerLayout
      ref={dragAnchorRef}
      title={title}
      explanation={explanation}
      tabs={tabs}
      body={body}
      footer={footer}
    />
  )

  const content = (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={ref}
      className={cc(['dali-settingslayer-container', { 'dali-settingslayer-active': isActive }])}
      onMouseDown={handleMouseDown}
    >
      <div className={cc(['dali-settingslayer'])}>
        {typeof children === 'function' ? children({ renderLayout }) : children}
      </div>
    </div>
  )

  return (
    <Popover
      open={open}
      className={className}
      side={side}
      align={align}
      virtualRef={virtualRef}
      sideOffset={sideOffset}
      hideArrow={hideArrow}
      onEscapeKeyDown={handleEscapeKeyDown}
      draggable={draggable}
      usePortal
      zIndex={0}
    >
      {draggable ? (
        <DraggableSettingsLayerWrapper dragAnchorRef={dragAnchorRef}>{content}</DraggableSettingsLayerWrapper>
      ) : (
        content
      )}
    </Popover>
  )
}
